// Client-side <head> management for SEO. The app is a CRA SPA, so per-page
// title/description/OG/canonical tags are set at runtime; the crawl-time
// defaults live in public/index.html and must be kept in sync with SITE_*.
export const SITE_URL = "https://touchtalk.am";
export const SITE_NAME = "Touch Talk";
export const SITE_DESCRIPTION =
  "Touch Talk NFC business cards — share your contacts, socials and links with a single tap. Design your own digital card and order it online.";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/card.jpg`;

interface PageMeta {
  title: string;
  description?: string;
  /** Path starting with "/" used for canonical + og:url. */
  path?: string;
  image?: string;
  type?: "website" | "article";
  /** Structured data; replaces the previous page's JSON-LD block. */
  jsonLd?: object;
  /** Robots directive. Set "noindex, nofollow" for internal/demo pages. */
  robots?: string;
  /** Written to <meta name="keywords"> (site-internal tags; Google ignores it). */
  keywords?: string[];
  /** ISO timestamps for article:published_time / article:modified_time. */
  publishedTime?: string;
  modifiedTime?: string;
  /** Absolute canonical URL; overrides the one derived from `path`. */
  canonical?: string;
}

const removeMeta = (attr: "name" | "property", key: string) => {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
};

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export const setPageMeta = ({
  title,
  description = SITE_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  robots,
  keywords,
  publishedTime,
  modifiedTime,
  canonical,
}: PageMeta) => {
  document.title = title;
  upsertMeta("name", "description", description);
  // Always written, so navigating from a noindex page back to a normal one
  // doesn't leave the directive behind on a SPA route change.
  upsertMeta("name", "robots", robots || "index, follow");

  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:type", type);
  upsertMeta("property", "og:image", image);
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", image);

  if (keywords && keywords.length) upsertMeta("name", "keywords", keywords.join(", "));
  else removeMeta("name", "keywords");
  if (publishedTime) upsertMeta("property", "article:published_time", publishedTime);
  else removeMeta("property", "article:published_time");
  if (modifiedTime) upsertMeta("property", "article:modified_time", modifiedTime);
  else removeMeta("property", "article:modified_time");

  if (path || canonical) {
    const url = canonical || SITE_URL + path;
    upsertMeta("property", "og:url", url);
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
  }

  const prev = document.getElementById("page-jsonld");
  if (prev) prev.remove();
  if (jsonLd) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "page-jsonld";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }
};

/** URL-safe slug from a post title; keeps unicode letters (Armenian etc.). */
export const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/['’"”«»]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

/** Blog category listing path: /blog/{category-slug}. */
export const blogCategoryPath = (categorySlug: string) => `/blog/${categorySlug}`;

/**
 * Canonical path for a blog post: /blog/{slug}. The backend generates and
 * stores the slug; legacy /blog/{id} and /blog/{slug-id} URLs 301 to it.
 * The slugified-title fallback covers responses from a pre-slug backend.
 */
export const blogPostPath = (post: { id: number; title?: string; slug?: string }) =>
  `/blog/${post.slug || slugify(post.title ?? "") || post.id}`;
